import React from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import Icon from "../../../components/AppIcon";

const ElevationProfile = ({ elevationData, trek }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-border/50">
          <p className="font-headline font-semibold text-foreground">{`Day ${label}: ${data?.location}`}</p>
          <p className="text-primary font-medium">{`Elevation: ${data?.elevation}m`}</p>
          {data?.isAcclimatization && (
            <p className="text-accent text-sm mt-1 flex items-center gap-1">
              <Icon name="Pause" size={14} />
              Acclimatization Day
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (!elevationData?.length) return null;

  const maxElev = Math.max(...elevationData.map((d) => d?.elevation));
  const minElev = Math.min(...elevationData.map((d) => d?.elevation));
  const acclimatizationDays = elevationData.filter((d) => d?.isAcclimatization)?.length;
  const totalAscent = maxElev - minElev;
  const maxDay = elevationData.find((d) => d?.elevation === maxElev)?.day;

  return (
    <div className="bg-[#fafaf7] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-secondary">Elevation</span> Profile
          </h2>
          <div className="w-16 h-1 bg-secondary rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Track your altitude progression throughout the trek. Acclimatization
            days are strategically placed to ensure your safety and comfort at
            high altitudes.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-border/50 shadow-sm p-6 mb-8">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={elevationData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#ede8df" />
                <XAxis
                  dataKey="day"
                  stroke="#8a8a7a"
                  fontSize={12}
                  tickFormatter={(value) => `Day ${value}`}
                />
                <YAxis
                  stroke="#8a8a7a"
                  fontSize={12}
                  tickFormatter={(value) => `${value}m`}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  y={3500}
                  stroke="#c4873a"
                  strokeDasharray="5 5"
                  label={{
                    value: "High Altitude (3,500m)",
                    position: "topRight",
                    fill: "#c4873a",
                    fontSize: 11,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="elevation"
                  stroke="#2d5016"
                  strokeWidth={3}
                  dot={{ fill: "#2d5016", strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: "#c4873a" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-border/50 p-6 text-center hover:shadow-sm transition-shadow">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="TrendingUp" size={22} className="text-primary" />
            </div>
            <h3 className="font-headline font-semibold text-foreground mb-2">Maximum Altitude</h3>
            <p className="text-2xl font-bold text-primary mb-1">{trek?.maxAltitude}</p>
            <p className="text-sm text-text-secondary">Reached on Day {maxDay}</p>
          </div>

          <div className="bg-white rounded-2xl border border-border/50 p-6 text-center hover:shadow-sm transition-shadow">
            <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Pause" size={22} className="text-accent" />
            </div>
            <h3 className="font-headline font-semibold text-foreground mb-2">Acclimatization Days</h3>
            <p className="text-2xl font-bold text-accent mb-1">{acclimatizationDays}</p>
            <p className="text-sm text-text-secondary">Rest days for proper altitude adjustment</p>
          </div>

          <div className="bg-white rounded-2xl border border-border/50 p-6 text-center hover:shadow-sm transition-shadow">
            <div className="w-14 h-14 rounded-xl bg-success/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Activity" size={22} className="text-success" />
            </div>
            <h3 className="font-headline font-semibold text-foreground mb-2">Total Ascent</h3>
            <p className="text-2xl font-bold text-success mb-1">{totalAscent}m</p>
            <p className="text-sm text-text-secondary">Cumulative elevation gain</p>
          </div>
        </div>

        <div className="mt-8 bg-white border border-warning/20 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <div>
              <h3 className="font-headline font-semibold text-foreground mb-2">Altitude Awareness</h3>
              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                Our itinerary follows the "climb high, sleep low" principle with
                strategic acclimatization days. Our experienced Sherpa guides
                monitor your health throughout the trek and carry emergency
                oxygen and medical supplies.
              </p>
              <div className="grid sm:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Prevention Tips:</h4>
                  <ul className="space-y-1 text-text-secondary">
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary inline-block" /> Stay hydrated (3-4 liters daily)</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary inline-block" /> Ascend gradually</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary inline-block" /> Listen to your body</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary inline-block" /> Avoid alcohol and smoking</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Warning Signs:</h4>
                  <ul className="space-y-1 text-text-secondary">
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-error inline-block" /> Severe headaches</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-error inline-block" /> Nausea and vomiting</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-error inline-block" /> Difficulty breathing</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-error inline-block" /> Loss of coordination</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElevationProfile;
